// Accessibility helpers shared by the chat surfaces (fullscreen chat, preview).
//
// WCAG 3.1.2 (Language of Parts): each assistant message must carry correct
// lang/dir metadata when its language differs from the page language, so
// screen readers switch pronunciation and RTL scripts (Urdu, Arabic) render
// right-to-left. The backend does not return per-message language metadata,
// so we combine the user's explicit language selection with script detection
// on the message text itself.

export interface MessageLangMeta {
  /** BCP-47 language code for the message, or undefined when it matches the page language. */
  lang?: string;
  /** "rtl" when the message should render right-to-left. */
  dir?: "rtl";
}

// Characters unique to Urdu (not used in standard Arabic).
const URDU_MARKERS = /[\u0679\u0688\u0691\u06BA\u06BE\u06C1\u06C2\u06C3\u06D2\u06D3]/;
// Arabic-script block (covers Arabic, Urdu, Persian, etc.).
const ARABIC_SCRIPT = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/g;
// Hebrew block (also RTL).
const HEBREW_SCRIPT = /[\u0590-\u05FF]/g;

/** True when a language code is written right-to-left. */
export function isRtlLanguage(code: string | undefined | null): boolean {
  if (!code) return false;
  const base = code.toLowerCase().split(/[-_]/)[0];
  return ["ar", "ur", "fa", "he", "ps", "sd", "ug", "yi", "ckb"].includes(base);
}

/**
 * Determine lang/dir metadata for one assistant message.
 * `selectedLanguage` is the user's explicit reply-language override ("" = auto).
 */
export function getMessageLangMeta(content: string, selectedLanguage?: string): MessageLangMeta {
  const text = (content || "").slice(0, 2000);
  const total = text.replace(/\s|[0-9.,:;!?()\-–—"'`\[\]{}<>/@#%&*+=_|\\~^$]/g, "").length;
  if (total === 0) {
    return selectedLanguage && isRtlLanguage(selectedLanguage)
      ? { lang: selectedLanguage, dir: "rtl" }
      : selectedLanguage ? { lang: selectedLanguage } : {};
  }
  const arabicCount = (text.match(ARABIC_SCRIPT) || []).length;
  const hebrewCount = (text.match(HEBREW_SCRIPT) || []).length;
  const rtlShare = (arabicCount + hebrewCount) / total;

  // Predominantly RTL script → mark the message RTL with a best-effort lang.
  if (rtlShare > 0.4) {
    let lang: string;
    if (selectedLanguage && isRtlLanguage(selectedLanguage)) lang = selectedLanguage;
    else if (hebrewCount > arabicCount) lang = "he";
    else lang = URDU_MARKERS.test(text) ? "ur" : "ar";
    return { lang, dir: "rtl" };
  }

  // LTR content: only tag lang when the user explicitly picked a non-RTL
  // language (an RTL selection with LTR content means the reply came back in
  // another language, so trust the content).
  if (selectedLanguage && !isRtlLanguage(selectedLanguage)) return { lang: selectedLanguage };
  return {};
}

// ── Chat screen-reader announcer (WCAG 4.1.3 Status Messages) ──────────────
// Pure state machine driving the one polite live region per chat surface.
// Rules: announce a concise typing status while loading; announce each
// completed assistant/agent reply exactly once; never re-read restored
// history (wholesale message replacement re-baselines instead of announcing).

export const TYPING_ANNOUNCEMENT = "Sending message. Assistant is typing…";

export interface ChatMessageLike {
  id: string;
  role: string;
  content: string;
}

export interface ChatAnnouncerState {
  /** Conversation the tracked messages belong to. */
  convoId: string | null;
  /** Id of the newest message seen last run (append detection). */
  lastMsgId: string | null;
  /** Message count seen last run (bulk-hydration detection). */
  msgCount: number;
  /** Newest assistant/agent reply id already announced (or baselined). */
  announcedReplyId: string;
  /** Current live-region text. */
  announcement: string;
}

/**
 * Compute the next announcer state. Baselines (announces nothing) whenever
 * the message list was replaced wholesale — conversation switch, async
 * server hydration, or localStorage restore — and only announces replies
 * that arrived as an incremental append to the already-tracked thread.
 */
export function computeChatAnnouncement(
  prev: ChatAnnouncerState | null,
  convoId: string | null,
  messages: ReadonlyArray<ChatMessageLike>,
  isLoading: boolean,
): ChatAnnouncerState {
  const last = messages.length > 0 ? messages[messages.length - 1] : null;
  const lastReply =
    [...messages].reverse().find((m) => m.role === "assistant" || m.role === "agent") ?? null;

  const isSameConvo = prev !== null && prev.convoId === convoId;
  // Append = the previously-seen newest message is still present. When the
  // tracked thread was empty, allow only tiny growth (first user send or a
  // single welcome message); a large jump from empty means async hydration.
  const isAppend =
    isSameConvo &&
    (prev.lastMsgId !== null
      ? messages.some((m) => m.id === prev.lastMsgId)
      : messages.length - prev.msgCount <= 2);

  const tracked = {
    convoId,
    lastMsgId: last?.id ?? null,
    msgCount: messages.length,
  };

  if (!isAppend) {
    // Re-baseline: whatever replies already exist were not typed "just now".
    return {
      ...tracked,
      announcedReplyId: lastReply?.id ?? "",
      announcement: isLoading ? TYPING_ANNOUNCEMENT : "",
    };
  }

  if (isLoading) {
    return { ...tracked, announcedReplyId: prev.announcedReplyId, announcement: TYPING_ANNOUNCEMENT };
  }

  if (lastReply && lastReply.id !== prev.announcedReplyId) {
    const prefix = lastReply.role === "agent" ? "Live agent replied: " : "Assistant replied: ";
    return {
      ...tracked,
      announcedReplyId: lastReply.id,
      announcement: prefix + lastReply.content.slice(0, 400),
    };
  }

  // Nothing new: keep the last reply announcement, but clear stale typing text.
  return {
    ...tracked,
    announcedReplyId: prev.announcedReplyId,
    announcement: prev.announcement === TYPING_ANNOUNCEMENT ? "" : prev.announcement,
  };
}

/** True when the user prefers reduced motion (safe in non-browser environments). */
export function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}
